/**
 * DAAily Operations — "Margin of Products" request schemas.
 *
 * Oliver's self-merge zone (`shared/src/operations*.ts`) — operations-scoped zod
 * lives here, never in the app-wide `schemas.ts`.
 *
 * Phase A ships the SHAPES only; the callables that consume them
 * (`operationsMarginConfig`, `operationsMarginOverrides`) are Phase B (Michael's
 * review + manual deploy — see `docs/OPERATIONS-MARGIN.md` § For Michael). The
 * Phase-A pages compute client-side from the checked-in fixture + provisional
 * assumptions and never call these.
 */
import { z } from 'zod';
import { MARGIN_COMMISSION_KEYS, MARGIN_DTC_CATEGORIES, MARGIN_FAMILIES, } from './operationsMargin.js';
/** zod v3 `z.enum` wants a mutable non-empty string tuple. */
const asEnum = (a) => a;
/** One Base/Downside/Upside assumption number. Percentages are fractions (0.2 = 20%). */
export const MarginBandSchema = z.object({
    base: z.number().finite(),
    downside: z.number().finite(),
    upside: z.number().finite(),
});
/**
 * The editable assumption surface — everything EXCEPT `fxToChf`, which is
 * inherited from the shared commissions/USL FX source and never editable here
 * (Michael's condition 2).
 */
export const MarginAssumptionsInputSchema = z.object({
    commissionPct: z.record(z.enum(asEnum(MARGIN_COMMISSION_KEYS)), MarginBandSchema),
    dtcPct: z.record(z.enum(asEnum(MARGIN_DTC_CATEGORIES)), MarginBandSchema),
    blendedRateChfPerHour: MarginBandSchema,
    deliveryFlatChf: z.number().finite().min(0).max(10000).optional(),
    familyDeliveryCostChf: z.record(z.enum(asEnum(MARGIN_FAMILIES)), MarginBandSchema),
    membershipVariantDeliveryCostChf: z.object({ new: MarginBandSchema, renewal: MarginBandSchema }),
    coverageVariantDeliveryCostChf: z.object({ standard: MarginBandSchema, event: MarginBandSchema }),
});
/** Salesforce column mapping for the weekly product pull (all optional). */
export const MarginSfdcColumnMapSchema = z.object({
    fields: z.record(z.string().trim().min(1), z.string().trim().min(1).max(120)).optional(),
    listPrice: z.record(z.enum(asEnum(['CHF', 'EUR', 'USD', 'GBP', 'MXN', 'CLP', 'CNY'])), z.string().trim().min(1).max(120)).optional(),
    values: z.record(z.string().trim().min(1), z.record(z.string(), z.string())).optional(),
});
/** Read the config doc (assumptions + pull config). */
export const OperationsMarginConfigGetSchema = z.object({ action: z.literal('configGet') });
/**
 * Replace the config doc (callable diffs old->new for the audit log).
 * `sfdcReportId` / `sfdcColumnMap` / `editorEmails` are optional — a plain
 * assumptions edit omits them and they're left unchanged.
 */
export const OperationsMarginConfigSetSchema = z.object({
    action: z.literal('configSet'),
    assumptions: MarginAssumptionsInputSchema,
    sfdcReportId: z.string().trim().max(20).optional(),
    sfdcColumnMap: MarginSfdcColumnMapSchema.optional(),
    editorEmails: z.array(z.string().trim().toLowerCase().email()).max(10).optional(),
    /** Operations admin only: whether approvals need a second editor (see MarginConfigDoc). */
    requireSecondApprover: z.boolean().optional(),
});
/** List the stored product rows (for the hub pages once wired off the fixture). */
export const OperationsMarginProductListSchema = z.object({ action: z.literal('productList') });
/** Operations-admin only: fetch the Salesforce report's column metadata, to
 *  fill `sfdcColumnMap` without shell access. */
export const OperationsMarginDescribeReportSchema = z.object({
    action: z.literal('describeReport'),
    reportId: z.string().trim().min(15).max(20).optional(),
});
/** One per-product exception. Null/omitted field → fall back to the family/category assumption. */
export const MarginOverrideInputSchema = z.object({
    productId: z.string().trim().min(1).max(64),
    family: z.enum(asEnum(MARGIN_FAMILIES)).nullable().optional(),
    commissionKey: z.enum(asEnum(MARGIN_COMMISSION_KEYS)).nullable().optional(),
    dtcCategory: z.enum(asEnum(MARGIN_DTC_CATEGORIES)).nullable().optional(),
    resourceCostChf: z.number().finite().nonnegative().nullable().optional(),
    dtcPct: z.number().finite().min(0).max(1).nullable().optional(),
    commissionPct: z.number().finite().min(0).max(1).nullable().optional(),
    excludeResourceCost: z.boolean().optional(),
    excludeDtc: z.boolean().optional(),
    excludeCommission: z.boolean().optional(),
    batchLabel: z.string().trim().max(80).nullable().optional(),
    batchSize: z.number().int().min(1).max(1000000).nullable().optional(),
    batchMode: z.enum(['covers', 'perUnit']).nullable().optional(),
    batchResourcePerUnit: z.boolean().optional(),
    note: z.string().trim().max(500).nullable().optional(),
});
export const OperationsMarginOverrideListSchema = z.object({ action: z.literal('overrideList') });
export const OperationsMarginOverrideUpsertSchema = z.object({
    action: z.literal('overrideUpsert'),
    override: MarginOverrideInputSchema,
});
export const OperationsMarginOverrideDeleteSchema = z.object({
    action: z.literal('overrideDelete'),
    productId: z.string().trim().min(1).max(64),
});
/* ─────────────── survey / review-candidate / follow-up / approval ──────────
 * Items 2–5: the Phase-2 audit made in-app instead of another spreadsheet.
 * Each collection supports list (view) + upsert (edit). The one-time SEED of
 * the audit data does NOT go through this callable — it never touches the web
 * bundle at all (Michael's review, Sep 2026: a client-side import action means
 * the seed rows ship as JSON in the public JS bundle, unauthenticated, which
 * defeats the deny-all Firestore rules entirely). The seed is a standalone
 * `scripts/migrate-margin-seed-review-suite.ts` (the existing migrate-* ADC
 * pattern), run once from a trusted machine. ─────────────────────────────── */
export const MarginSurveyHoursSchema = z.object({
    editorialSocial: z.number().finite().nullable().optional(),
    editorialContent: z.number().finite().nullable().optional(),
    editorialCatalog: z.number().finite().nullable().optional(),
    pmContent: z.number().finite().nullable().optional(),
    pmPsm: z.number().finite().nullable().optional(),
    adOps: z.number().finite().nullable().optional(),
    psmRaw: z.string().trim().max(40).nullable().optional(),
    psmSelected: z.number().finite().nullable().optional(),
    design: z.number().finite().nullable().optional(),
    tech: z.number().finite().nullable().optional(),
    ai: z.number().finite().nullable().optional(),
});
export const MarginSurveyEntryInputSchema = z.object({
    productId: z.string().trim().min(1).max(64),
    productName: z.string().trim().min(1).max(200),
    family: z.string().trim().max(60),
    platform: z.string().trim().max(40),
    hours: MarginSurveyHoursSchema,
    coverage: z.string().trim().max(40).nullable().optional(),
    contentNotes: z.string().trim().max(1000).nullable().optional(),
    source: z.enum(['phase2-audit-import', 'manual']).optional(),
});
export const OperationsMarginSurveyListSchema = z.object({ action: z.literal('surveyList') });
export const OperationsMarginSurveyUpsertSchema = z.object({
    action: z.literal('surveyUpsert'),
    entry: MarginSurveyEntryInputSchema,
});
export const MarginReviewCandidateInputSchema = z.object({
    productId: z.string().trim().min(1).max(64),
    productName: z.string().trim().min(1).max(200),
    classification: z.string().trim().max(60),
    priority: z.string().trim().max(20),
    primaryField: z.string().trim().max(80).nullable().optional(),
    currentAssumptionNote: z.string().trim().max(500).nullable().optional(),
    surveySignalNote: z.string().trim().max(500).nullable().optional(),
    maxResourceCostImpactChf: z.number().finite().nullable().optional(),
    currentCm2Chf: z.number().finite().nullable().optional(),
    sensitivityRangeNote: z.string().trim().max(120).nullable().optional(),
    reason: z.string().trim().max(500).nullable().optional(),
    recommendedAction: z.string().trim().max(500).nullable().optional(),
    suggestedFamily: z.string().trim().max(60).nullable().optional(),
    suggestedFamilyNote: z.string().trim().max(500).nullable().optional(),
    source: z.enum(['phase2-audit-import', 'manual']).optional(),
});
export const OperationsMarginReviewListSchema = z.object({ action: z.literal('reviewList') });
export const MarginFollowUpInputSchema = z.object({
    id: z.string().trim().min(1).max(80),
    source: z.enum(['follow-up', 'data-quality', 'manual']),
    priority: z.string().trim().max(20).nullable().optional(),
    team: z.string().trim().max(60).nullable().optional(),
    productId: z.string().trim().max(64).nullable().optional(),
    productName: z.string().trim().max(200).nullable().optional(),
    question: z.string().trim().min(1).max(1000),
    whyItMatters: z.string().trim().max(1000).nullable().optional(),
    relatedField: z.string().trim().max(120).nullable().optional(),
    status: z.enum(['open', 'resolved']),
});
/** One row of `MarginConfigDoc.costCategoryEvidence` — used only by the
 *  seed migration (this is reference data, never edited through the API). */
export const MarginSurveyEvidenceItemSchema = z.object({
    category: z.string().trim().min(1).max(40),
    label: z.string().trim().min(1).max(80),
    filledProducts: z.number().int().min(0),
    evidenceLevel: z.string().trim().min(1).max(20),
    keyFinding: z.string().trim().max(300).nullable().optional(),
    recommendedNextStep: z.string().trim().max(300).nullable().optional(),
});
export const OperationsMarginFollowUpListSchema = z.object({ action: z.literal('followUpList') });
export const OperationsMarginFollowUpUpsertSchema = z.object({
    action: z.literal('followUpUpsert'),
    item: MarginFollowUpInputSchema,
});
/** Generic approval — "select for approval" (item 5) on ANY data point,
 *  addressed by the stable string `key` from `marginApprovalKey()`. Restricted
 *  to a safe charset (Michael's review): the key becomes a Firestore doc id,
 *  and `/` in particular would write to an unintended subpath. */
export const OperationsMarginApprovalListSchema = z.object({ action: z.literal('approvalList') });
export const OperationsMarginApprovalSetSchema = z.object({
    action: z.literal('approvalSet'),
    key: z.string().trim().min(1).max(160).regex(/^[A-Za-z0-9 :_.-]+$/, 'key has an unsupported character'),
    approved: z.boolean(),
    note: z.string().trim().max(300).nullable().optional(),
    /** The value being approved (a short fingerprint), kept so a later change can be flagged. */
    value: z.string().trim().max(200).nullable().optional(),
});
/** Saved sets: named, private to their owner. Tried values and calculator test products. */
export const MARGIN_SAVED_SETS_MAX_PER_OWNER = 50;
const nullableNumber = z.number().finite().nullable();
export const MarginTryOutSetPayloadSchema = z.object({
    changes: z.record(z.string().max(160), z.number().finite()).refine((o) => Object.keys(o).length <= 300, 'too many changes'),
});
export const MarginTestProductPayloadSchema = z.object({
    templateId: z.string().trim().max(64).nullable(),
    description: z.string().trim().max(1000).optional(),
    platform: z.string().trim().max(40).optional(),
    family: z.string().trim().min(1).max(80),
    commissionKey: z.string().trim().min(1).max(80),
    dtcCategory: z.string().trim().min(1).max(80),
    variant: z.enum(['', 'new', 'renewal', 'standard', 'event']),
    price: z.number().finite().min(0).max(1e9),
    costMode: z.enum(['hours', 'family', 'manual']),
    hours: z.record(z.string().max(40), z.number().finite().min(0).max(100000)).refine((o) => Object.keys(o).length <= 20, 'too many hour fields'),
    rateIn: nullableNumber,
    flatIn: nullableNumber,
    override: z.object({
        resourceCostChf: nullableNumber.optional(),
        dtcPct: nullableNumber.optional(),
        commissionPct: nullableNumber.optional(),
        excludeResourceCost: z.boolean().optional(),
        excludeDtc: z.boolean().optional(),
        excludeCommission: z.boolean().optional(),
    }),
});
export const MarginMembershipPlanPayloadSchema = z.object({
    state: z.object({
        bundleId: z.string().trim().max(64),
        addons: z.record(z.string().max(64), z.number().int().min(0).max(999)).refine((o) => Object.keys(o).length <= 30, 'too many add-ons'),
        geoLimited: z.boolean(),
        geoPct: z.string().max(12),
        year1: z.boolean(),
        target: z.string().max(12),
        ccy: z.string().max(4),
        band: z.enum(['base', 'downside', 'upside']).optional(),
    }),
    plan: z.array(z.object({
        id: z.string().trim().min(1).max(64),
        layer: z.enum(['core', 'bundle', 'addon']),
        name: z.string().trim().max(160),
        forWhom: z.string().max(400),
        priceChf: z.number().finite().min(0).max(1e9),
        commissionKey: z.string().trim().max(80),
        dtcCategory: z.string().trim().max(80),
        lines: z.array(z.object({
            id: z.string().trim().min(1).max(64),
            label: z.string().max(400),
            qty: z.number().finite().min(0).max(100000),
            hoursEach: z.number().finite().min(0).max(100000),
            chfEach: z.number().finite().min(0).max(1e9),
            year1Only: z.boolean(),
            source: z.enum(['survey', 'estimate']),
            listProduct: z.string().max(160).optional(),
        })).max(60),
    })).max(30),
});
const SavedSetNameSchema = z.string().trim().min(1).max(80);
const SavedSetIdSchema = z.string().trim().min(1).max(64).regex(/^[A-Za-z0-9_-]+$/, 'id has an unsupported character');
export const MarginSavedSetInputSchema = z.discriminatedUnion('kind', [
    z.object({ kind: z.literal('tryOutSet'), id: SavedSetIdSchema.optional(), name: SavedSetNameSchema, payload: MarginTryOutSetPayloadSchema }),
    z.object({ kind: z.literal('testProduct'), id: SavedSetIdSchema.optional(), name: SavedSetNameSchema, payload: MarginTestProductPayloadSchema }),
    z.object({ kind: z.literal('membershipPlan'), id: SavedSetIdSchema.optional(), name: SavedSetNameSchema, payload: MarginMembershipPlanPayloadSchema }),
]);
export const OperationsMarginSavedSetListSchema = z.object({ action: z.literal('savedSetList') });
export const OperationsMarginSavedSetUpsertSchema = z.object({ action: z.literal('savedSetUpsert'), set: MarginSavedSetInputSchema });
export const OperationsMarginSavedSetDeleteSchema = z.object({ action: z.literal('savedSetDelete'), id: SavedSetIdSchema });
/** Discriminated union over every `operationsMarginApi` action. */
export const OperationsMarginRequestSchema = z.discriminatedUnion('action', [
    OperationsMarginConfigGetSchema,
    OperationsMarginConfigSetSchema,
    OperationsMarginProductListSchema,
    OperationsMarginDescribeReportSchema,
    OperationsMarginOverrideListSchema,
    OperationsMarginOverrideUpsertSchema,
    OperationsMarginOverrideDeleteSchema,
    OperationsMarginSurveyListSchema,
    OperationsMarginSurveyUpsertSchema,
    OperationsMarginReviewListSchema,
    OperationsMarginFollowUpListSchema,
    OperationsMarginFollowUpUpsertSchema,
    OperationsMarginApprovalListSchema,
    OperationsMarginApprovalSetSchema,
    OperationsMarginSavedSetListSchema,
    OperationsMarginSavedSetUpsertSchema,
    OperationsMarginSavedSetDeleteSchema,
]);
//# sourceMappingURL=operationsMarginSchemas.js.map